import { Loader2 } from 'lucide-react'

import type { SalesOrder, SalesOrderItem } from '@/lib/api/sales-order-api'

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useSalesOrder } from '@/hooks/use-sales-order-query'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

interface Props {
    orderId: string | null
    onClose: () => void
}

export function StationOrderDetailDialog({ orderId, onClose }: Props) {
    const { data: order, isLoading } = useSalesOrder(orderId ?? '')
    const open = orderId !== null

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next) onClose()
            }}
        >
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto font-mono">
                <DialogHeader>
                    <DialogTitle>
                        {m.station_order_detail_title({
                            orderCode: order?.order_code ?? '',
                        })}
                    </DialogTitle>
                </DialogHeader>

                {isLoading || !order ? (
                    <div className="flex items-center justify-center py-10 text-gray-500">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        {m.common_loading()}
                    </div>
                ) : (
                    <OrderDetailBody order={order} />
                )}
            </DialogContent>
        </Dialog>
    )
}

function OrderDetailBody({ order }: { order: SalesOrder }) {
    return (
        <div className="space-y-5 text-sm">
            <section className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-gray-700">
                <Field label={m.order_customer()} value={order.customer_name} />
                <Field
                    label={m.customer_phone()}
                    value={order.customer_phone || '—'}
                />
                <Field
                    label={m.customer_shipping_address()}
                    value={order.shipping_address || '—'}
                    full
                />
                {order.product_notes ? (
                    <Field
                        label={m.customer_product_notes()}
                        value={order.product_notes}
                        full
                    />
                ) : null}
            </section>

            <ItemsTable items={order.items ?? []} />

            <div className="flex justify-end pt-2 border-t border-gray-200">
                <div className="text-base font-bold text-gray-900">
                    {m.order_grand_total()}: {formatVnd(order.total_amount)}
                </div>
            </div>
        </div>
    )
}

function ItemsTable({ items }: { items: SalesOrderItem[] }) {
    if (items.length === 0) {
        return (
            <div className="py-6 text-center text-gray-400">
                {m.station_order_detail_empty()}
            </div>
        )
    }
    return (
        <table className="w-full text-sm">
            <thead>
                <tr className="text-xs text-gray-500 uppercase tracking-wider border-b border-gray-300 bg-gray-50">
                    <th className="text-left py-2 px-3">{m.product_name()}</th>
                    <th className="text-right py-2 px-3 w-16">
                        {m.order_quantity()}
                    </th>
                    <th className="text-right py-2 px-3 w-28">
                        {m.order_unit_price()}
                    </th>
                    <th className="text-right py-2 px-3 w-32">
                        {m.order_subtotal()}
                    </th>
                </tr>
            </thead>
            <tbody>
                {items.map((item) => (
                    <tr key={item.id} className="border-b border-gray-100">
                        <td className="py-2 px-3 text-gray-800">
                            {item.product_name}
                            {item.product_sku ? (
                                <span className="ml-2 text-xs text-gray-400">
                                    {item.product_sku}
                                </span>
                            ) : null}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-800">
                            {item.quantity}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-600">
                            {formatVnd(item.unit_price)}
                        </td>
                        <td className="py-2 px-3 text-right font-medium text-gray-900">
                            {formatVnd(item.subtotal)}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    )
}

function Field({
    label,
    value,
    full,
}: {
    label: string
    value: string
    full?: boolean
}) {
    return (
        <div className={full ? 'sm:col-span-2' : ''}>
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">
                {label}
            </div>
            <div className="text-gray-900">{value}</div>
        </div>
    )
}
